import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../ThemeContext';
import { usePreferences, TableKey } from '../PreferencesContext';
import CustomSelect from './CustomSelect';
import {
  getPricingFormulas,
  upsertPricingFormula,
  deletePricingFormula,
  validatePricingFormula,
} from '../api';

const TABLE_COLUMN_DEFS: { tableKey: TableKey; label: string; columns: { key: string; label: string }[] }[] = [
  {
    tableKey: 'product_list',
    label: 'Product List',
    columns: [
      { key: 'thumbnail', label: 'Thumbnail' },
      { key: 'sku', label: 'SKU' },
      { key: 'title', label: 'Title' },
      { key: 'brand', label: 'Brand' },
      { key: 'ean', label: 'EAN' },
      { key: 'price', label: 'Price' },
      { key: 'buybox', label: 'Buy Box' },
      { key: 'suggested', label: 'Suggested' },
      { key: 'status', label: 'Status' },
    ],
  },
  {
    tableKey: 'mappings_table',
    label: 'Mappings Table',
    columns: [
      { key: 'thumbnail', label: 'Thumbnail' },
      { key: 'marketplace', label: 'Marketplace' },
      { key: 'asin', label: 'ASIN' },
      { key: 'amazon_title', label: 'Amazon Title' },
      { key: 'buybox', label: 'Buy Box' },
      { key: 'suggested', label: 'Suggested' },
      { key: 'confidence', label: 'Confidence' },
      { key: 'source', label: 'Source' },
      { key: 'verified', label: 'Verified' },
    ],
  },
  {
    tableKey: 'candidates_table',
    label: 'Candidates Table',
    columns: [
      { key: 'marketplace', label: 'Marketplace' },
      { key: 'asin', label: 'ASIN' },
      { key: 'amazon_title', label: 'Amazon Title' },
      { key: 'score_title', label: 'Title Score' },
      { key: 'score_brand', label: 'Brand Score' },
      { key: 'score_ean', label: 'EAN Score' },
      { key: 'score_attr', label: 'Attr Score' },
      { key: 'score_composite', label: 'Composite' },
      { key: 'status', label: 'Status' },
    ],
  },
];

interface AppSettings {
  keepaApiKey: string;
  apiBaseUrl: string;
  defaultMarketplace: string;
  autoMatchOnImport: boolean;
  matchConfidenceThreshold: number;
  notificationsEnabled: boolean;
  compactTableView: boolean;
  itemsPerPage: number;
}

const STORAGE_KEY = 'asin-tracker-settings';

const defaultSettings: AppSettings = {
  keepaApiKey: '',
  apiBaseUrl: 'http://localhost:8000',
  defaultMarketplace: 'GB',
  autoMatchOnImport: true,
  matchConfidenceThreshold: 75,
  notificationsEnabled: true,
  compactTableView: false,
  itemsPerPage: 20,
};

const MARKETPLACES = [
  { code: 'GB', domain: 'amazon.co.uk' },
  { code: 'US', domain: 'amazon.com' },
  { code: 'DE', domain: 'amazon.de' },
  { code: 'FR', domain: 'amazon.fr' },
  { code: 'IT', domain: 'amazon.it' },
  { code: 'ES', domain: 'amazon.es' },
];

interface FormulaState {
  formula: string;
  saved: string;      // last persisted value
  saving: boolean;
  preview: string | null;
  previewError: string | null;
  samplePrice: string;
}

type FormulasMap = Record<string, FormulaState>;

// ── Auto-Match Hierarchy ──────────────────────────────────────────────────────

export interface MatchCriterion {
  key: 'ean' | 'title' | 'asin';
  label: string;
  desc: string;
  enabled: boolean;
}

export const DEFAULT_MATCH_HIERARCHY: MatchCriterion[] = [
  { key: 'ean',   label: 'EAN / UPC',      desc: 'Exact barcode match (highest confidence)',    enabled: true  },
  { key: 'title', label: 'Product Title',   desc: 'Fuzzy title similarity (≥ 60% token overlap)', enabled: true  },
  { key: 'asin',  label: 'ASIN',            desc: 'Existing ASIN mapping match',                 enabled: false },
];

export const MATCH_HIERARCHY_KEY = 'asin-tracker-match-hierarchy';

export function loadMatchHierarchy(): MatchCriterion[] {
  try {
    const stored = JSON.parse(localStorage.getItem(MATCH_HIERARCHY_KEY) || 'null');
    if (Array.isArray(stored) && stored.length > 0) return stored;
  } catch { /* ignore */ }
  return DEFAULT_MATCH_HIERARCHY;
}

function saveMatchHierarchy(h: MatchCriterion[]) {
  localStorage.setItem(MATCH_HIERARCHY_KEY, JSON.stringify(h));
}

interface MatchingSectionProps {
  settings: AppSettings;
  updateSetting: <K extends keyof AppSettings>(key: K, value: AppSettings[K]) => void;
}

const MatchingSection: React.FC<MatchingSectionProps> = ({ settings, updateSetting }) => {
  const [hierarchy, setHierarchy] = useState<MatchCriterion[]>(loadMatchHierarchy);
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [saved, setSaved] = useState(false);

  const updateHierarchy = (next: MatchCriterion[]) => {
    setHierarchy(next);
    saveMatchHierarchy(next);
    setSaved(true);
    setTimeout(() => setSaved(false), 1500);
  };

  const toggleEnabled = (idx: number) => {
    const next = hierarchy.map((c, i) => i === idx ? { ...c, enabled: !c.enabled } : c);
    updateHierarchy(next);
  };

  const moveUp = (idx: number) => {
    if (idx === 0) return;
    const next = [...hierarchy];
    [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
    updateHierarchy(next);
  };

  const moveDown = (idx: number) => {
    if (idx === hierarchy.length - 1) return;
    const next = [...hierarchy];
    [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
    updateHierarchy(next);
  };

  const handleDragStart = (idx: number) => setDragIdx(idx);
  const handleDragOver = (e: React.DragEvent, idx: number) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === idx) return;
    const next = [...hierarchy];
    const item = next.splice(dragIdx, 1)[0];
    next.splice(idx, 0, item);
    setDragIdx(idx);
    setHierarchy(next);
  };
  const handleDrop = () => {
    if (dragIdx === null) return;
    saveMatchHierarchy(hierarchy);
    setSaved(true);
    setTimeout(() => setSaved(false), 1500);
    setDragIdx(null);
  };

  return (
    <>
      <div className="card">
        <div className="card-header">
          <span className="card-title">Matching Configuration</span>
        </div>

        <div className="settings-item">
          <div className="settings-item-info">
            <div className="settings-item-label">Auto-Match on Import</div>
            <div className="settings-item-desc">Automatically run matching when products are imported via bulk import</div>
          </div>
          <label className="toggle-switch">
            <input
              type="checkbox"
              checked={settings.autoMatchOnImport}
              onChange={(e) => updateSetting('autoMatchOnImport', e.target.checked)}
            />
            <span className="toggle-slider" />
          </label>
        </div>

        <div className="settings-item" style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
          <div className="settings-item-info" style={{ marginBottom: '12px' }}>
            <div className="settings-item-label">Minimum Confidence Threshold</div>
            <div className="settings-item-desc">
              Candidates below this threshold will require manual review ({settings.matchConfidenceThreshold}%)
            </div>
          </div>
          <div style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '16px' }}>
            <input
              type="range"
              min={0}
              max={100}
              value={settings.matchConfidenceThreshold}
              onChange={(e) => updateSetting('matchConfidenceThreshold', parseInt(e.target.value))}
              className="settings-range"
            />
            <span style={{ fontWeight: 600, color: 'var(--accent)', minWidth: '40px' }}>
              {settings.matchConfidenceThreshold}%
            </span>
          </div>
        </div>
      </div>

      <div className="card" style={{ marginTop: '12px' }}>
        <div className="card-header">
          <span className="card-title">Auto-Match Search Hierarchy</span>
          {saved && (
            <span style={{ fontSize: '12px', color: 'var(--success)', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
              Saved
            </span>
          )}
        </div>

        <p style={{ color: 'var(--text-muted)', fontSize: '13px', marginBottom: '16px' }}>
          When linking Keepa search results to internal products, the system tries each enabled method in order.
          Drag rows or use arrows to reorder. The first successful match wins.
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {hierarchy.map((criterion, idx) => (
            <div
              key={criterion.key}
              draggable
              onDragStart={() => handleDragStart(idx)}
              onDragOver={(e) => handleDragOver(e, idx)}
              onDrop={handleDrop}
              onDragEnd={() => setDragIdx(null)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: '10px 14px',
                border: '1px solid var(--border-primary)',
                borderRadius: 'var(--radius-md)',
                background: dragIdx === idx ? 'var(--accent-soft)' : 'var(--bg-card-hover)',
                cursor: 'grab',
                opacity: criterion.enabled ? 1 : 0.5,
                transition: 'background 0.15s',
              }}
            >
              {/* Drag handle */}
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="2" style={{ flexShrink: 0, cursor: 'grab' }}>
                <line x1="8" y1="6" x2="16" y2="6"/><line x1="8" y1="12" x2="16" y2="12"/><line x1="8" y1="18" x2="16" y2="18"/>
              </svg>

              {/* Rank badge */}
              <span style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: 22, height: 22, borderRadius: '50%',
                background: criterion.enabled ? 'var(--accent)' : 'var(--bg-secondary)',
                color: criterion.enabled ? '#fff' : 'var(--text-muted)',
                fontSize: '11px', fontWeight: 700, flexShrink: 0,
              }}>
                {idx + 1}
              </span>

              {/* Label + desc */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: '13.5px', color: 'var(--text-primary)' }}>{criterion.label}</div>
                <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{criterion.desc}</div>
              </div>

              {/* Up / Down */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', flexShrink: 0 }}>
                <button
                  className="btn btn-secondary btn-small"
                  style={{ padding: '2px 6px', lineHeight: 1 }}
                  onClick={() => moveUp(idx)}
                  disabled={idx === 0}
                  title="Move up"
                >
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="18 15 12 9 6 15"/>
                  </svg>
                </button>
                <button
                  className="btn btn-secondary btn-small"
                  style={{ padding: '2px 6px', lineHeight: 1 }}
                  onClick={() => moveDown(idx)}
                  disabled={idx === hierarchy.length - 1}
                  title="Move down"
                >
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="6 9 12 15 18 9"/>
                  </svg>
                </button>
              </div>

              {/* Enable toggle */}
              <label className="toggle-switch" style={{ flexShrink: 0 }}>
                <input type="checkbox" checked={criterion.enabled} onChange={() => toggleEnabled(idx)} />
                <span className="toggle-slider" />
              </label>
            </div>
          ))}
        </div>

        <div style={{ marginTop: '12px' }}>
          <button
            className="btn btn-secondary btn-small"
            onClick={() => updateHierarchy(DEFAULT_MATCH_HIERARCHY)}
          >
            Reset to defaults
          </button>
        </div>
      </div>
    </>
  );
};

const Settings: React.FC = () => {
  const { theme, toggleTheme } = useTheme();
  const { columnVisibility, toggleColumn, resetAllColumns, saveStatus } = usePreferences();
  const [settings, setSettings] = useState<AppSettings>(defaultSettings);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [activeSection, setActiveSection] = useState<string>('general');

  // Pricing formulas state
  const [formulas, setFormulas] = useState<FormulasMap>(() =>
    Object.fromEntries(
      MARKETPLACES.map((mp) => [
        mp.code,
        { formula: '', saved: '', saving: false, preview: null, previewError: null, samplePrice: '10.00' },
      ])
    )
  );
  const [formulasLoading, setFormulasLoading] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        setSettings({ ...defaultSettings, ...JSON.parse(stored) });
      } catch { /* ignore */ }
    }
  }, []);

  useEffect(() => {
    if (activeSection !== 'pricing') return;
    setFormulasLoading(true);
    getPricingFormulas()
      .then((res) => {
        const entries: { marketplace_code: string; formula: string }[] = res.data.formulas;
        setFormulas((prev) => {
          const next = { ...prev };
          entries.forEach(({ marketplace_code, formula }) => {
            if (next[marketplace_code]) {
              next[marketplace_code] = { ...next[marketplace_code], formula, saved: formula };
            }
          });
          return next;
        });
      })
      .catch(() => {})
      .finally(() => setFormulasLoading(false));
  }, [activeSection]);

  const handleSave = () => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    setMessage('Settings saved successfully');
    setError('');
    setTimeout(() => setMessage(''), 3000);
  };

  const handleReset = () => {
    if (!window.confirm('Reset all settings to defaults? This cannot be undone.')) return;
    setSettings(defaultSettings);
    localStorage.removeItem(STORAGE_KEY);
    setMessage('Settings reset to defaults');
    setError('');
    setTimeout(() => setMessage(''), 3000);
  };

  const updateSetting = <K extends keyof AppSettings>(key: K, value: AppSettings[K]) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  const updateFormula = (code: string, patch: Partial<FormulaState>) => {
    setFormulas((prev) => ({ ...prev, [code]: { ...prev[code], ...patch } }));
  };

  const handleSaveFormula = useCallback(async (code: string) => {
    const state = formulas[code];
    if (!state.formula.trim()) {
      // Empty → delete the formula
      updateFormula(code, { saving: true, preview: null, previewError: null });
      try {
        await deletePricingFormula(code);
        updateFormula(code, { saved: '', saving: false });
        setMessage(`Formula for ${code} cleared`);
        setTimeout(() => setMessage(''), 3000);
      } catch (err: any) {
        updateFormula(code, { saving: false });
        setError(err.response?.data?.detail || 'Failed to clear formula');
      }
      return;
    }
    updateFormula(code, { saving: true, preview: null, previewError: null });
    try {
      await upsertPricingFormula(code, state.formula.trim());
      updateFormula(code, { saved: state.formula.trim(), saving: false });
      setMessage(`Formula for ${code} saved`);
      setError('');
      setTimeout(() => setMessage(''), 3000);
    } catch (err: any) {
      updateFormula(code, { saving: false });
      setError(err.response?.data?.detail || 'Failed to save formula');
    }
  }, [formulas]);

  const handlePreviewFormula = useCallback(async (code: string) => {
    const state = formulas[code];
    const samplePrice = parseFloat(state.samplePrice);
    if (!state.formula.trim() || isNaN(samplePrice)) return;
    updateFormula(code, { preview: null, previewError: null });
    try {
      const res = await validatePricingFormula(state.formula.trim(), samplePrice);
      if (res.data.valid) {
        updateFormula(code, { preview: res.data.result?.toFixed(2) ?? null, previewError: null });
      } else {
        updateFormula(code, { preview: null, previewError: res.data.error || 'Invalid formula' });
      }
    } catch {
      updateFormula(code, { preview: null, previewError: 'Validation failed' });
    }
  }, [formulas]);

  const sections = [
    { id: 'general', label: 'General', icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="3" />
        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
      </svg>
    )},
    { id: 'api', label: 'API Configuration', icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="16 18 22 12 16 6" />
        <polyline points="8 6 2 12 8 18" />
      </svg>
    )},
    { id: 'matching', label: 'Matching', icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
        <polyline points="22 4 12 14.01 9 11.01" />
      </svg>
    )},
    { id: 'appearance', label: 'Appearance', icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="5" />
        <line x1="12" y1="1" x2="12" y2="3" />
        <line x1="12" y1="21" x2="12" y2="23" />
        <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
        <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
        <line x1="1" y1="12" x2="3" y2="12" />
        <line x1="21" y1="12" x2="23" y2="12" />
        <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
        <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
      </svg>
    )},
    { id: 'columns', label: 'Table Columns', icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" />
        <rect x="3" y="14" width="7" height="7" /><rect x="14" y="14" width="7" height="7" />
      </svg>
    )},
    { id: 'pricing', label: 'Pricing Formulas', icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
        <line x1="12" y1="1" x2="12" y2="23" />
        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
      </svg>
    )},
  ];

  return (
    <div>
      <div className="page-header">
        <h2>Settings</h2>
        <p>Configure application preferences and integrations</p>
      </div>

      {message && <div className="alert alert-success">{message}</div>}
      {error && <div className="alert alert-error">{error}</div>}

      <div className="settings-layout">
        {/* Settings Sidebar/Tabs */}
        <div className="settings-nav">
          {sections.map((s) => (
            <button
              key={s.id}
              className={`settings-nav-item ${activeSection === s.id ? 'active' : ''}`}
              onClick={() => setActiveSection(s.id)}
            >
              <span className="settings-nav-icon">{s.icon}</span>
              <span>{s.label}</span>
            </button>
          ))}
        </div>

        {/* Settings Content */}
        <div className="settings-content">
          {activeSection === 'general' && (
            <div className="card">
              <div className="card-header">
                <span className="card-title">General Settings</span>
              </div>

              <div className="settings-item">
                <div className="settings-item-info">
                  <div className="settings-item-label">Default Marketplace</div>
                  <div className="settings-item-desc">The default marketplace for Keepa searches</div>
                </div>
                <CustomSelect
                  value={settings.defaultMarketplace}
                  onChange={(val) => updateSetting('defaultMarketplace', val)}
                  options={[
                    { value: 'GB', label: 'GB - amazon.co.uk' },
                    { value: 'US', label: 'US - amazon.com' },
                    { value: 'DE', label: 'DE - amazon.de' },
                    { value: 'FR', label: 'FR - amazon.fr' },
                    { value: 'IT', label: 'IT - amazon.it' },
                    { value: 'ES', label: 'ES - amazon.es' },
                  ]}
                />
              </div>

              <div className="settings-item">
                <div className="settings-item-info">
                  <div className="settings-item-label">Items Per Page</div>
                  <div className="settings-item-desc">Number of items to show in product list tables</div>
                </div>
                <CustomSelect
                  value={String(settings.itemsPerPage)}
                  onChange={(val) => updateSetting('itemsPerPage', parseInt(val))}
                  options={[
                    { value: '10', label: '10 items' },
                    { value: '20', label: '20 items' },
                    { value: '50', label: '50 items' },
                    { value: '100', label: '100 items' },
                  ]}
                />
              </div>

              <div className="settings-item">
                <div className="settings-item-info">
                  <div className="settings-item-label">Browser Notifications</div>
                  <div className="settings-item-desc">Show notifications when background tasks complete</div>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={settings.notificationsEnabled}
                    onChange={(e) => updateSetting('notificationsEnabled', e.target.checked)}
                  />
                  <span className="toggle-slider" />
                </label>
              </div>
            </div>
          )}

          {activeSection === 'api' && (
            <div className="card">
              <div className="card-header">
                <span className="card-title">API Configuration</span>
              </div>

              <div className="form-group">
                <label>Backend API URL</label>
                <input
                  value={settings.apiBaseUrl}
                  onChange={(e) => updateSetting('apiBaseUrl', e.target.value)}
                  placeholder="http://localhost:8000"
                />
                <p className="settings-field-hint">The base URL for the ASIN Tracker backend API</p>
              </div>

              <div className="form-group">
                <label>Keepa API Key</label>
                <input
                  type="password"
                  value={settings.keepaApiKey}
                  onChange={(e) => updateSetting('keepaApiKey', e.target.value)}
                  placeholder="Enter your Keepa API key"
                />
                <p className="settings-field-hint">Required for product searches. Get your key from keepa.com</p>
              </div>
            </div>
          )}

          {activeSection === 'matching' && (
            <MatchingSection settings={settings} updateSetting={updateSetting} />
          )}

          {activeSection === 'appearance' && (
            <div className="card">
              <div className="card-header">
                <span className="card-title">Appearance</span>
              </div>

              <div className="settings-item">
                <div className="settings-item-info">
                  <div className="settings-item-label">Theme</div>
                  <div className="settings-item-desc">Switch between dark and light mode</div>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    className={`btn btn-small ${theme === 'dark' ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => { if (theme !== 'dark') toggleTheme(); }}
                  >
                    Dark
                  </button>
                  <button
                    className={`btn btn-small ${theme === 'light' ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => { if (theme !== 'light') toggleTheme(); }}
                  >
                    Light
                  </button>
                </div>
              </div>

              <div className="settings-item">
                <div className="settings-item-info">
                  <div className="settings-item-label">Compact Table View</div>
                  <div className="settings-item-desc">Reduce padding in table rows for denser data display</div>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={settings.compactTableView}
                    onChange={(e) => updateSetting('compactTableView', e.target.checked)}
                  />
                  <span className="toggle-slider" />
                </label>
              </div>
            </div>
          )}

          {activeSection === 'columns' && (
            <div className="card">
              <div className="card-header">
                <span className="card-title">Table Column Visibility</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span className={`column-save-status column-save-status-${saveStatus}`}>
                    {saveStatus === 'saving' && (
                      <>
                        <svg className="column-toggle-spinner" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                          <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                        </svg>
                        Saving...
                      </>
                    )}
                    {saveStatus === 'saved' && (
                      <>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                        Saved
                      </>
                    )}
                    {saveStatus === 'error' && (
                      <>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" />
                        </svg>
                        Failed to save
                      </>
                    )}
                  </span>
                  <button className="btn btn-secondary btn-small" onClick={() => { resetAllColumns(); }}>
                    Reset All to Defaults
                  </button>
                </div>
              </div>

              <p style={{ color: 'var(--text-muted)', fontSize: '13px', marginBottom: '16px' }}>
                Choose which columns are visible in each table. Changes are saved automatically to your account.
                At least 2 columns must remain visible per table.
              </p>

              {TABLE_COLUMN_DEFS.map((tableDef) => {
                const vis = columnVisibility[tableDef.tableKey] || {};
                const visibleCount = Object.values(vis).filter(Boolean).length;
                const hiddenCount = tableDef.columns.length - visibleCount;
                return (
                  <div key={tableDef.tableKey} style={{ marginBottom: '20px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                      <span style={{ fontWeight: 600, fontSize: '14px', color: 'var(--text-primary)' }}>
                        {tableDef.label}
                      </span>
                      {hiddenCount > 0 && (
                        <span style={{ fontSize: '12px', color: 'var(--text-muted)', background: 'var(--bg-secondary)', padding: '1px 8px', borderRadius: '10px' }}>
                          {hiddenCount} hidden
                        </span>
                      )}
                    </div>
                    <div className="column-settings-grid">
                      {tableDef.columns.map((col) => {
                        const checked = vis[col.key] !== false;
                        const disabled = checked && visibleCount <= 2;
                        return (
                          <label key={col.key} className={`column-settings-item ${disabled ? 'disabled' : ''}`}>
                            <input
                              type="checkbox"
                              checked={checked}
                              disabled={disabled}
                              onChange={() => toggleColumn(tableDef.tableKey, col.key)}
                            />
                            <span>{col.label}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {activeSection === 'pricing' && (
            <div className="card">
              <div className="card-header">
                <span className="card-title">Pricing Formulas</span>
              </div>

              {/* Reference block */}
              <div style={{
                background: 'var(--accent-soft)',
                border: '1px solid var(--border-accent)',
                borderRadius: 'var(--radius-md)',
                padding: '12px 16px',
                marginBottom: '20px',
                fontSize: '13px',
                lineHeight: 1.6,
                color: 'var(--text-secondary)',
              }}>
                <div style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: '4px' }}>
                  How formulas work
                </div>
                Use{' '}
                <code style={{ background: 'var(--accent-soft)', color: 'var(--accent)', padding: '1px 5px', borderRadius: '4px', fontFamily: 'monospace', border: '1px solid var(--border-accent)' }}>basePrice</code>
                {' '}(Buy Box) and{' '}
                <code style={{ background: 'var(--accent-soft)', color: 'var(--accent)', padding: '1px 5px', borderRadius: '4px', fontFamily: 'monospace', border: '1px solid var(--border-accent)' }}>costPrice</code>
                {' '}(product cost, 0 if not set) to build an expression.
                Example: <code style={{ fontFamily: 'monospace', color: 'var(--text-primary)' }}>(basePrice + 1.95) * 1.21</code>
              </div>

              {formulasLoading ? (
                <div className="loading"><div className="spinner" style={{ width: 20, height: 20 }} /></div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {MARKETPLACES.map((mp) => {
                    const state = formulas[mp.code];
                    const isDirty = state.formula !== state.saved;
                    const hasFormula = !!state.saved;
                    return (
                      <div key={mp.code} style={{
                        border: '1px solid var(--border-primary)',
                        borderRadius: 'var(--radius-lg)',
                        overflow: 'hidden',
                      }}>
                        {/* Card header */}
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          padding: '10px 16px',
                          background: 'var(--bg-card)',
                          borderBottom: '1px solid var(--border-primary)',
                        }}>
                          <span style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 30,
                            height: 30,
                            borderRadius: 'var(--radius-sm)',
                            background: 'var(--accent-soft)',
                            color: 'var(--accent)',
                            fontWeight: 700,
                            fontSize: '12px',
                            letterSpacing: '0.03em',
                            flexShrink: 0,
                          }}>{mp.code}</span>
                          <div style={{ flex: 1 }}>
                            <span style={{ fontWeight: 600, fontSize: '13.5px', color: 'var(--text-primary)', marginRight: '8px' }}>
                              {mp.code}
                            </span>
                            <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{mp.domain}</span>
                          </div>
                          {hasFormula && !isDirty && (
                            <span style={{
                              display: 'inline-flex', alignItems: 'center', gap: '4px',
                              fontSize: '11px', fontWeight: 600, color: 'var(--success)',
                              background: 'rgba(63,185,80,0.1)', border: '1px solid rgba(63,185,80,0.25)',
                              padding: '2px 9px', borderRadius: '20px',
                            }}>
                              <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="20 6 9 17 4 12"/>
                              </svg>
                              Active
                            </span>
                          )}
                          {isDirty && (
                            <span style={{
                              fontSize: '11px', fontWeight: 600, color: 'var(--accent)',
                              background: 'var(--accent-soft)', border: '1px solid var(--border-accent)',
                              padding: '2px 9px', borderRadius: '20px',
                            }}>Unsaved</span>
                          )}
                          {!hasFormula && !isDirty && (
                            <span style={{
                              fontSize: '11px', color: 'var(--text-muted)',
                              background: 'var(--bg-card-hover)', border: '1px solid var(--border-primary)',
                              padding: '2px 9px', borderRadius: '20px',
                            }}>No formula</span>
                          )}
                        </div>

                        {/* Card body */}
                        <div style={{ padding: '14px 16px', background: 'var(--bg-card-hover)', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                          {/* Formula row */}
                          <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
                            <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                              <label style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-muted)', marginBottom: '6px', display: 'block' }}>
                                Formula
                              </label>
                              <input
                                style={{ fontFamily: 'monospace', fontSize: '13px' }}
                                placeholder="e.g. (basePrice + 1.95) * 1.21"
                                value={state.formula}
                                onChange={(e) => updateFormula(mp.code, { formula: e.target.value, preview: null, previewError: null })}
                                onKeyDown={(e) => { if (e.key === 'Enter') handleSaveFormula(mp.code); }}
                              />
                            </div>
                            <button
                              className="btn btn-primary"
                              onClick={() => handleSaveFormula(mp.code)}
                              disabled={state.saving || !isDirty}
                              style={{ height: 40, flexShrink: 0, minWidth: '80px', alignSelf: 'flex-end' }}
                            >
                              {state.saving ? (
                                <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                  <svg className="column-toggle-spinner" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                    <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
                                  </svg>
                                  Saving
                                </span>
                              ) : 'Save'}
                            </button>
                          </div>

                          {/* Preview row */}
                          <div style={{
                            display: 'flex', alignItems: 'flex-end', gap: '8px', flexWrap: 'wrap',
                            paddingTop: '10px', borderTop: '1px solid var(--border-primary)',
                          }}>
                            <div className="form-group" style={{ marginBottom: 0 }}>
                              <label style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-muted)', marginBottom: '6px', display: 'block' }}>
                                Test Buy Box price
                              </label>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                style={{ width: '110px' }}
                                value={state.samplePrice}
                                onChange={(e) => updateFormula(mp.code, { samplePrice: e.target.value, preview: null, previewError: null })}
                              />
                            </div>
                            <button
                              className="btn btn-secondary"
                              style={{ height: 40, flexShrink: 0 }}
                              onClick={() => handlePreviewFormula(mp.code)}
                              disabled={!state.formula.trim()}
                            >
                              Run Preview
                            </button>
                            {state.preview !== null && (
                              <div style={{
                                height: 40, display: 'flex', alignItems: 'center',
                                gap: '6px', paddingLeft: '4px',
                              }}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
                                </svg>
                                <span style={{ fontSize: '15px', fontWeight: 700, color: 'var(--accent)' }}>
                                  {state.samplePrice.startsWith('£') ? '' : ''}{state.preview}
                                </span>
                              </div>
                            )}
                            {state.previewError && (
                              <div style={{
                                height: 40, display: 'flex', alignItems: 'center',
                                gap: '5px', fontSize: '12px', color: 'var(--danger)',
                              }}>
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                  <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                                </svg>
                                {state.previewError}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'space-between', flexWrap: 'wrap' }}>
            <button className="btn btn-danger btn-small" onClick={handleReset}>
              Reset to Defaults
            </button>
            <button className="btn btn-primary" onClick={handleSave}>
              Save Settings
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
